import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FriendCounts, FriendItem, MutedWord, RelationKind, RelationListItem, SuggestedUser, UserProfile, UserSummary } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { toast } from "../../components/ui/Toast";
import { confirm } from "../../components/ui/Dialog";

export type Relationship = UserProfile["relationship"];

export const useFriends = (tab: "all" | "online" | "pending", enabled = true) =>
  useQuery({ queryKey: ["friends", tab], queryFn: () => api.get<{ items: FriendItem[] }>(`/api/friends?tab=${tab}`).then((r) => r.items), enabled });

export const useFriendCounts = (enabled = true) => useQuery({ queryKey: ["friend-counts"], queryFn: () => api.get<FriendCounts>("/api/friends/counts"), enabled, staleTime: 15_000 });

export const useRelations = (kind: RelationKind, enabled = true) =>
  useQuery({ queryKey: ["relations", kind], queryFn: () => api.get<{ items: RelationListItem[] }>(`/api/relations?kind=${kind}`).then((r) => r.items), enabled });

export const useMutedWords = () => useQuery({ queryKey: ["relations", "muted-words"], queryFn: () => api.get<{ items: MutedWord[] }>("/api/relations/muted-words").then((r) => r.items) });

export const useSuggestions = (limit = 8, enabled = true) =>
  useQuery({ queryKey: ["suggestions", limit], queryFn: () => api.get<{ items: SuggestedUser[] }>(`/api/suggestions/people?limit=${limit}`).then((r) => r.items), enabled, staleTime: 120_000 });

/**
 * Relationship changes with the same toasts and cache updates everywhere
 * (profile header, friends page, settings lists). Reversible changes offer
 * undo; blocking asks first because it removes follows and friendship.
 */
export function useRelationshipActions() {
  const qc = useQueryClient();
  const refresh = useCallback(() => {
    for (const key of ["friends", "friend-counts", "relations", "profile", "suggestions", "feed", "follow-list"]) void qc.invalidateQueries({ queryKey: [key] });
  }, [qc]);

  const setRelation = useCallback(
    async (user: Pick<UserSummary, "id" | "displayName">, kind: RelationKind, value: boolean, opts: { silent?: boolean } = {}) => {
      if (kind === "block" && value) {
        const ok = await confirm({
          title: `Block ${user.displayName}?`,
          description: "You won't see each other's posts, comments or messages, and they can't follow you or send friend requests. Follows and friendship between you are removed.",
          confirmLabel: "Block",
          danger: true,
        });
        if (!ok) return false;
      }
      try {
        await api.put<Relationship>(`/api/relations/${user.id}/${kind}`, { value });
        refresh();
        if (!opts.silent) {
          const verb: Record<RelationKind, [string, string]> = {
            block: ["Blocked", "Unblocked"],
            mute: ["Muted", "Unmuted"],
            restrict: ["Restricted", "Unrestricted"],
            close_friend: ["Added to close friends:", "Removed from close friends:"],
          };
          toast.show(`${verb[kind][value ? 0 : 1]} ${user.displayName}`, { label: "Undo", onClick: () => void setRelation(user, kind, !value, { silent: true }) });
        }
        return true;
      } catch (e) {
        toast.error(errorMessage(e));
        return false;
      }
    },
    [refresh],
  );

  const friend = useCallback(
    async (user: Pick<UserSummary, "id" | "displayName">, action: "request" | "accept" | "remove", current?: Relationship["friend"]) => {
      try {
        if (action === "request") await api.post(`/api/friends/${user.id}`);
        else if (action === "accept") await api.post(`/api/friends/${user.id}/accept`);
        else {
          if (current === "friends") {
            const ok = await confirm({ title: `Remove ${user.displayName} as a friend?`, confirmLabel: "Remove", danger: true });
            if (!ok) return;
          }
          await api.del(`/api/friends/${user.id}`);
        }
        refresh();
        if (action === "request") toast.show("Friend request sent");
        if (action === "accept") toast.show(`You and ${user.displayName} are now friends`);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    },
    [refresh],
  );

  return { setRelation, friend, refresh };
}
